

import React from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon, Clock, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { format } from "date-fns";

interface DateTimeFilterProps {
  date: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  timeSlot: string;
  onTimeSlotChange: (timeSlot: string) => void;
  timeSlots: string[];
  isDateDisabled: (date: Date) => boolean;
}

const DateTimeFilter: React.FC<DateTimeFilterProps> = ({ 
  date, 
  onDateChange, 
  timeSlot, 
  onTimeSlotChange, 
  timeSlots, 
  isDateDisabled 
}) => {
  const { t } = useTranslation('filters');

  // Create display name mapping for time slots
  const getTimeSlotDisplayName = (slot: string) => {
    if (slot === 'any_time') return t('time.anyTime');
    // Capitalize first letter for other time slots
    return slot.charAt(0).toUpperCase() + slot.slice(1);
  };

  return (
    <>
      <div>
        <h3 className="text-sm font-medium mb-2 flex items-center">
          <CalendarIcon className="mr-2 h-4 w-4 text-amber-600" /> {t('date.label')}
        </h3>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={"outline"}
              className={`w-full border-amber-200 justify-start text-left font-normal ${
                !date && "text-muted-foreground"
              }`}
            >
              {date ? (
                format(date, "PPP")
              ) : (
                t('date.selectDate')
              )}
              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={onDateChange}
              disabled={isDateDisabled}
              initialFocus
              className="p-3 pointer-events-auto"
            />
          </PopoverContent>
        </Popover>
        {date && (
          <Alert className="mt-2 border-amber-200 bg-amber-50/50">
            <Info className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-xs text-amber-800">
              {t('date.availabilityWarning')}
            </AlertDescription>
          </Alert>
        )}
      </div>

      <div>
        <h3 className="text-sm font-medium mb-2 flex items-center">
          <Clock className="mr-2 h-4 w-4 text-amber-600" /> {t('time.label')}
        </h3>
        <Select value={timeSlot} onValueChange={onTimeSlotChange}>
          <SelectTrigger className="border-amber-200">
            <SelectValue placeholder={t('time.selectTime')} />
          </SelectTrigger>
          <SelectContent>
            {timeSlots.map((slot) => (
              <SelectItem key={slot} value={slot}>
                {getTimeSlotDisplayName(slot)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  );
};

export default DateTimeFilter;
